import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ReactReduxContext, useSelector } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import config from '@plone/volto/registry';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import starEmptySVG from '@plone/volto/icons/star-empty.svg';
import starSolidSVG from '../../icons/star-solid.svg';
import { initFavorites, toggleFavorite, useFavorites } from './favoritesStore';

const messages = defineMessages({
  mark: {
    id: 'favorite_mark',
    defaultMessage: 'Mark as favorite',
  },
  unmark: {
    id: 'favorite_unmark',
    defaultMessage: 'Remove favorite',
  },
});

/**
 * Star toggle that marks a session as favorite. Logged-in users get
 * persistence in their profile via the @favorites REST service;
 * anonymous users get localStorage persistence in their browser.
 */
const FavoriteToggle = ({ uid, token, className }) => {
  const intl = useIntl();
  const { favorites } = useFavorites();
  // The public apiPath has no ++api++ prefix in this deployment; the
  // relative path works in dev (proxy) and production (rewrite) alike.
  const configured = config.settings.apiPath;
  const apiPath = configured?.includes('++api++') ? configured : '/++api++';

  useEffect(() => {
    initFavorites(token, apiPath);
  }, [token, apiPath]);

  if (!uid) return null;

  const active = favorites.includes(uid);
  const onClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    toggleFavorite(uid, token, apiPath);
  };

  const label = intl.formatMessage(active ? messages.unmark : messages.mark);
  return (
    <button
      type="button"
      className={['favorite-toggle', active && 'is-active', className]
        .filter(Boolean)
        .join(' ')}
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
    >
      <Icon name={active ? starSolidSVG : starEmptySVG} size="18px" />
    </button>
  );
};

FavoriteToggle.propTypes = {
  uid: PropTypes.string.isRequired,
  token: PropTypes.string,
  className: PropTypes.string,
};

const FavoriteToggleWithStore = (props) => {
  const token = useSelector((state) => state.userSession?.token);
  return <FavoriteToggle {...props} token={token} />;
};

/**
 * Renders the store-connected toggle when a redux Provider exists,
 * otherwise the anonymous (localStorage) variant — so bare renders in
 * tests and non-redux contexts do not crash.
 */
const FavoriteToggleContainer = (props) => {
  const store = useContext(ReactReduxContext)?.store;
  if (!store) return <FavoriteToggle {...props} token={null} />;
  return <FavoriteToggleWithStore {...props} />;
};

export default FavoriteToggleContainer;
